import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  ChevronDown,
  Plus,
  X,
  Edit3,
  Calendar,
  Tag,
  MessageSquare,
  FileText,
  Sparkles,
} from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ALL_CLASSES } from '@/lib/ontology/schema'
import { useConnectionStore } from '@/stores/connectionStore'
import { getLocalName } from '@/lib/ontology/namespaces'
import { ENTITY_TYPE_NAMESPACES, resolveEntityType } from '@/lib/interviewer/entityCreation'
import { InstanceDetailPanel } from '@/components/shared/InstanceDetailPanel'
import { CreateInstanceDialog } from '@/components/shared/CreateInstanceDialog'
import { CanvasInstanceNavigator } from './CanvasInstanceNavigator'
import { CreateCanvasInstanceDialog } from './CreateCanvasInstanceDialog'
import { PhotoSectionCard } from './PhotoSectionCard'
import { SectionSuggestionsPopup } from './SectionSuggestionsPopup'
import { SectionSuggestionsPanel } from './SectionSuggestionsPanel'
import { CanvasCoachPanel, type EntityControlFunctions } from './CanvasCoachPanel'
import { useAIStore } from '@/stores/aiStore'
import { UnifiedDocumentPanel } from '@/components/document-processing'
import { useAuthStore } from '@/stores/authStore'
import { useProjectStore } from '@/stores/projectStore'
import type { Document2WithEntities, Document2Entity } from '@/lib/api/client'
import { canvasAIApi, sparqlApi } from '@/lib/api/client'
import {
  CANVAS_TEMPLATES,
  BUSINESS_MODEL_CANVAS,
  type CanvasTemplate,
  type CanvasSection,
} from './canvasTemplates'
import {
  useCanvasInstances,
  useCreateCanvasInstance,
  useUpdateCanvasInstance,
  useCanvasInstance,
  useCanvasSectionData,
  useUpdateSectionNotes,
  useEnsureDefaultCanvas,
  useLinkItemToSection,
  useSyncCanvasEntities,
} from '@/hooks/useCanvasInstances'
import type { ExtractedEntity } from '@/hooks/useCanvasCoach'
import { cn } from '@/lib/utils'

// Map template IDs to canvas types for Canvas Coach
// These must match the CanvasType enum in the backend and ai_workflows.canvas_type in the database
const TEMPLATE_TO_CANVAS_TYPE: Record<string, string> = {
  'business-model-canvas': 'bmc',
  'value-proposition-canvas': 'vpc',
  'positioning-canvas': 'positioning',
  'problem-statement-canvas': 'psc',
  'persona-canvas': 'persona',
}

// Default instance names per template
const DEFAULT_INSTANCE_NAMES: Record<string, string> = {
  'business-model-canvas': 'Main Business Model',
  'value-proposition-canvas': 'Main Value Proposition',
  'positioning-canvas': 'Main Positioning',
  'problem-statement-canvas': 'Main Problem Statement',
  'persona-canvas': 'Primary Persona',
}

interface CanvasExplorerProps {
  /** Hide AI Coach and Document panels (e.g., when embedded in workspace) */
  hideAuxiliaryPanels?: boolean
}

export function CanvasExplorer({ hideAuxiliaryPanels = false }: CanvasExplorerProps = {}) {
  const { isConnected } = useConnectionStore()
  const { currentAccount } = useAuthStore()
  const { currentProject } = useProjectStore()
  const { apiKey } = useAIStore()
  const [selectedTemplate, setSelectedTemplate] = useState<CanvasTemplate>(BUSINESS_MODEL_CANVAS)
  const [selectedItem, setSelectedItem] = useState<{ uri: string; sectionId: string } | null>(null)
  const [expandedSection, setExpandedSection] = useState<string | null>(null)
  const [createForSection, setCreateForSection] = useState<CanvasSection | null>(null)

  // Canvas instance state
  const [selectedInstanceUri, setSelectedInstanceUri] = useState<string | null>(null)
  const [, setSelectedInstanceName] = useState<string | null>(null)
  const [showCreateInstanceDialog, setShowCreateInstanceDialog] = useState(false)

  // Property editing state
  const [showPropertyPane, setShowPropertyPane] = useState(false)

  // AI Panel state
  const [showAIPanel, setShowAIPanel] = useState(false)
  const [focusedSectionId, setFocusedSectionId] = useState<string | null>(null)

  // Document Panel state
  const [showDocumentPanel, setShowDocumentPanel] = useState(false)

  // Suggestions Panel state
  const [showSuggestionsPanel, setShowSuggestionsPanel] = useState(false)
  const [suggestionsForSection, setSuggestionsForSection] = useState<CanvasSection | null>(null)

  // Pending entities from AI coach (shown directly on canvas sections)
  const [pendingEntities, setPendingEntities] = useState<ExtractedEntity[]>([])
  const [entityControls, setEntityControls] = useState<EntityControlFunctions | null>(null)

  // Get instances for selected template
  const queryClient = useQueryClient()
  const { data: instances = [], isLoading: instancesLoading } = useCanvasInstances(selectedTemplate.id)
  const createCanvasInstance = useCreateCanvasInstance()
  const updateSectionNotes = useUpdateSectionNotes()
  const linkItemToSection = useLinkItemToSection()
  const ensureDefaultCanvas = useEnsureDefaultCanvas()
  const syncCanvasEntities = useSyncCanvasEntities()

  // Get full instance details for property pane
  const { data: instanceDetails } = useCanvasInstance(selectedInstanceUri)

  // Get section data for AI context
  const { data: sectionData = [] } = useCanvasSectionData(selectedInstanceUri)

  // Build canvas context from section data for AI assistant
  const canvasContext = useMemo(() => {
    const context: Record<string, string[]> = {}
    for (const section of sectionData) {
      const items: string[] = []
      // Add notes if present
      if (section.notes?.trim()) {
        items.push(section.notes)
      }
      // Add linked item labels
      for (const item of section.linkedItems || []) {
        if (item.label) {
          items.push(item.label)
        }
        if (item.note) {
          items.push(item.note)
        }
      }
      if (items.length > 0) {
        context[section.sectionId] = items
      }
    }
    return context
  }, [sectionData])

  // Convert canvas context to string for AI duplicate detection
  const canvasContentString = useMemo(() => {
    const parts: string[] = []
    for (const [sectionId, items] of Object.entries(canvasContext)) {
      if (items.length > 0) {
        parts.push(`${sectionId}: ${items.join(', ')}`)
      }
    }
    return parts.join('\n')
  }, [canvasContext])

  // Auto-select first instance or create default when template changes
  useEffect(() => {
    if (instancesLoading || !isConnected) return

    if (instances.length > 0) {
      // Select first instance if none selected
      if (!selectedInstanceUri || !instances.find(i => i.uri === selectedInstanceUri)) {
        setSelectedInstanceUri(instances[0].uri)
        setSelectedInstanceName(instances[0].name)
      }
    } else {
      // No instances - auto-create default with auto-populate
      const sections = selectedTemplate.sections.map(section => ({
        sectionId: section.id,
        classUris: section.classUris,
      }))

      const canvasType = TEMPLATE_TO_CANVAS_TYPE[selectedTemplate.id] || 'bmc'

      ensureDefaultCanvas.mutate(
        {
          templateId: selectedTemplate.id,
          templateName: selectedTemplate.name,
          canvasType,
          sections,
        },
        {
          onSuccess: (result) => {
            if (result.created) {
              console.log(`Created default ${selectedTemplate.name} canvas and auto-populated`)
            }
            // Instances will be refetched automatically, which will trigger selection
          },
        }
      )
    }
  }, [instances, instancesLoading, selectedTemplate.id, isConnected])

  // Initialize Canvas AI session when canvas is selected
  useEffect(() => {
    if (!selectedInstanceUri || !currentAccount?.id || !currentProject?.id) return

    // Map template ID to canvas type for the API
    const canvasType = TEMPLATE_TO_CANVAS_TYPE[selectedTemplate.id] || 'bmc'

    // Initialize session (creates if doesn't exist)
    canvasAIApi.getSession(
      currentAccount.id,
      currentProject.id,
      selectedInstanceUri,
      canvasType
    ).catch(error => {
      console.warn('Failed to initialize Canvas AI session:', error)
    })
  }, [selectedInstanceUri, currentAccount?.id, currentProject?.id, selectedTemplate.id])

  // Sync canvas with new entities from graph when canvas loads
  // This picks up entities created elsewhere (e.g., Entity Tree)
  useEffect(() => {
    if (!selectedInstanceUri || !isConnected || syncCanvasEntities.isPending) return

    const sections = selectedTemplate.sections.map(section => ({
      sectionId: section.id,
      classUris: section.classUris,
    }))

    syncCanvasEntities.mutate({
      canvasUri: selectedInstanceUri,
      sections,
    })
  }, [selectedInstanceUri, selectedTemplate.id, isConnected])

  // Reset selection when changing canvas template
  const handleTemplateChange = (template: CanvasTemplate) => {
    setSelectedTemplate(template)
    setSelectedItem(null)
    setExpandedSection(null)
    setSelectedInstanceUri(null)
    setSelectedInstanceName(null)
    setShowPropertyPane(false)
    setFocusedSectionId(null)
  }

  // Toggle AI panel with keyboard shortcut
  const toggleAIPanel = useCallback(() => {
    setShowAIPanel((prev) => !prev)
  }, [])

  // Toggle Document panel with keyboard shortcut
  const toggleDocumentPanel = useCallback(() => {
    setShowDocumentPanel((prev) => !prev)
  }, [])

  // Keyboard shortcuts: Cmd/Ctrl+I (AI), Cmd/Ctrl+D (Documents)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
        e.preventDefault()
        toggleAIPanel()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
        e.preventDefault()
        toggleDocumentPanel()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [toggleAIPanel, toggleDocumentPanel])

  // Handle section focus for AI context
  const handleSectionFocus = useCallback((sectionId: string) => {
    setFocusedSectionId(sectionId)
    // Auto-open AI panel when focusing a section
    if (!showAIPanel) {
      setShowAIPanel(true)
    }
  }, [showAIPanel])

  const handleSelectInstance = (uri: string, name: string) => {
    setSelectedInstanceUri(uri)
    setSelectedInstanceName(name)
    setSelectedItem(null) // Clear item selection when switching instances
  }

  const handleInstanceCreated = (uri: string, name: string) => {
    setShowCreateInstanceDialog(false)
    setSelectedInstanceUri(uri)
    setSelectedInstanceName(name)
  }

  // Handle document analysis completion (entity-first pipeline via documents3)
  const handleDocumentAnalyzed = useCallback((result: Document2WithEntities) => {
    console.log('Document analyzed:', result.document.title)
    console.log(`Found ${result.entities.length} entities stored in graph`)
    // Note: Entities are stored in Fuseki graph via documents3
    // User can browse via Documents3 page or entity selection callback
  }, [])

  // Handle entity selection from the document panel
  const handleEntitySelected = useCallback((entity: Document2Entity, doc: Document2WithEntities) => {
    console.log('Entity selected:', entity.name, 'from document:', doc.document.title)
    // TODO: Could highlight the entity or navigate to detail view
  }, [])

  // Handle adding extracted entity to canvas section
  // Creates a proper ontology instance via backend and links it to the canvas section
  const handleAddEntityToCanvas = useCallback(async (entity: ExtractedEntity, sectionId: string) => {
    if (!selectedInstanceUri) {
      console.error('Cannot add entity: no canvas instance selected')
      return
    }

    if (!currentAccount?.id || !currentProject?.id) {
      console.error('Cannot add entity: no account or project context')
      return
    }

    try {
      // Step 1: Resolve entity type to ontology class
      const resolved = resolveEntityType(entity.type)
      if (!resolved.entityType) {
        console.warn(`Unknown entity type: ${entity.type}, falling back to notes`)
        // Fallback: add to notes if type is unknown
        const currentSection = sectionData.find(s => s.sectionId === sectionId)
        const currentNotes = currentSection?.notes || ''
        const entityBullet = `• ${entity.label}${entity.description ? ` - ${entity.description}` : ''}`
        const newNotes = currentNotes ? `${currentNotes}\n${entityBullet}` : entityBullet
        await updateSectionNotes.mutateAsync({ canvasUri: selectedInstanceUri, sectionId, notes: newNotes })
        return
      }

      // Step 2: Get the ontology class URI for linking
      const typeConfig = ENTITY_TYPE_NAMESPACES[resolved.entityType]
      if (!typeConfig) {
        console.error(`No namespace config for entity type: ${resolved.entityType}`)
        return
      }
      const typeUri = `${typeConfig.namespace}${resolved.entityType}`

      console.log('Creating ontology instance via backend:', {
        canvasUri: selectedInstanceUri,
        sectionId,
        entityType: resolved.entityType,
        label: entity.label,
      })

      // Step 3: Create instance in knowledge graph via backend API
      // This ensures proper project graph isolation and namespace handling
      const { entityUri: instanceUri } = await sparqlApi.createEntity(
        currentAccount.id,
        currentProject.id,
        {
          entityType: resolved.entityType,
          label: entity.label,
          description: entity.description,
        }
      )

      console.log('Instance created via backend:', instanceUri)

      // Step 4: Link instance to canvas section
      await linkItemToSection.mutateAsync({
        canvasUri: selectedInstanceUri,
        sectionId,
        itemUri: instanceUri,
        itemLabel: entity.label,
        itemType: typeUri,
        note: entity.description,
      })

      // Invalidate queries to refresh tree view
      queryClient.invalidateQueries({ queryKey: ['instances'] })
      queryClient.invalidateQueries({ queryKey: ['instanceCount'] })
      queryClient.invalidateQueries({ queryKey: ['ontologyInstances'] })
      queryClient.invalidateQueries({ queryKey: ['entityCounts'] })

      console.log('Entity added to knowledge graph and linked to canvas:', instanceUri)
    } catch (err) {
      console.error('Failed to add entity to knowledge graph:', err)
    }
  }, [selectedInstanceUri, sectionData, updateSectionNotes, linkItemToSection, currentAccount, currentProject, queryClient])

  // Handle entities change from Canvas Coach
  const handleEntitiesChange = useCallback((entities: ExtractedEntity[]) => {
    setPendingEntities(entities)
  }, [])

  // Handle controls ready from Canvas Coach
  const handleControlsReady = useCallback((controls: EntityControlFunctions) => {
    setEntityControls(controls)
  }, [])

  // Handle approving a pending entity from canvas section
  const handleApproveEntity = useCallback(async (entity: PendingEntity, sectionId: string) => {
    // Add to canvas via SPARQL
    await handleAddEntityToCanvas(entity as ExtractedEntity, sectionId)
    // Remove from pending entities via coach hook
    entityControls?.approveEntity(entity as ExtractedEntity)
  }, [handleAddEntityToCanvas, entityControls])

  // Handle dismissing a pending entity from canvas section
  const handleDismissEntity = useCallback((entity: PendingEntity) => {
    entityControls?.dismissEntity(entity as ExtractedEntity)
  }, [entityControls])

  // Handle adding a suggestion from the AI suggestions popup/panel
  const handleAddSuggestion = useCallback(async (suggestion: { type: string; label: string; description?: string; sectionId: string; confidence: number }) => {
    const entity: ExtractedEntity = {
      type: suggestion.type,
      label: suggestion.label,
      description: suggestion.description,
      sectionId: suggestion.sectionId,
      confidence: suggestion.confidence,
    }
    await handleAddEntityToCanvas(entity, suggestion.sectionId)
  }, [handleAddEntityToCanvas])

  // Handle opening suggestions panel for a section
  const handleOpenSuggestions = useCallback((section: CanvasSection) => {
    setSuggestionsForSection(section)
    setShowSuggestionsPanel(true)
  }, [])

  // Handle closing suggestions panel
  const handleCloseSuggestions = useCallback(() => {
    setShowSuggestionsPanel(false)
    // Don't clear suggestionsForSection immediately - let animation complete
    setTimeout(() => setSuggestionsForSection(null), 200)
  }, [])


  if (!isConnected) {
    return (
      <div className="h-full flex items-center justify-center">
        <Card className="max-w-[400px] text-center p-8">
          <AlertCircle className="w-12 h-12 text-warning mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Not Connected</h3>
          <p className="text-text-muted text-sm">
            Connect to Fuseki in Settings to view the canvas.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col gap-4">
      {/* Template Tabs - Tab navigation instead of dropdown */}
      <div className="flex-shrink-0 border-b border-warm-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 overflow-x-auto pb-0">
            {CANVAS_TEMPLATES.map((template) => (
              <button
                key={template.id}
                onClick={() => handleTemplateChange(template)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-[1px]',
                  selectedTemplate.id === template.id
                    ? 'text-primary border-primary bg-white'
                    : 'text-text-secondary hover:text-text hover:bg-warm-50 border-transparent'
                )}
              >
                {template.name}
              </button>
            ))}
          </div>
          {/* Panel Toggles - Hidden when embedded in workspace */}
          {!hideAuxiliaryPanels && (
            <div className="flex items-center gap-2 mr-2">
              <button
                onClick={toggleDocumentPanel}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                  showDocumentPanel
                    ? 'bg-solution/10 text-solution'
                    : 'text-text-secondary hover:bg-warm-100'
                )}
                title="Toggle Documents (Cmd+D)"
              >
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Documents</span>
              </button>
              <button
                onClick={toggleAIPanel}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                  showAIPanel
                    ? 'bg-primary/10 text-primary'
                    : 'text-text-secondary hover:bg-warm-100'
                )}
                title="Toggle AI Assistant (Cmd+I)"
              >
                <MessageSquare className="w-4 h-4" />
                <span className="hidden sm:inline">AI Assistant</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Template Description */}
      <div className="flex-shrink-0 text-sm text-text-muted px-1">
        {selectedTemplate.description}
      </div>

      {/* Instance Navigator */}
      <CanvasInstanceNavigator
        templateId={selectedTemplate.id}
        selectedInstanceUri={selectedInstanceUri}
        onSelectInstance={handleSelectInstance}
        onCreateNew={() => setShowCreateInstanceDialog(true)}
      />

      {/* Canvas Content with Property Pane and AI Panel */}
      <div className="flex-1 flex min-h-0">
        {/* Canvas Grid */}
        <div className="flex-1 min-w-0 overflow-auto">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${selectedTemplate.gridColumns}, 1fr)`,
              gridTemplateRows: `repeat(${selectedTemplate.gridRows}, 1fr)`,
              gridTemplateAreas: selectedTemplate.gridAreas,
              gap: '12px',
              minHeight: '550px',
              height: '100%',
              padding: '4px',
            }}
          >
            {selectedTemplate.sections.map((section) =>
              section.sectionType === 'photo' ? (
                <PhotoSectionCard
                  key={section.id}
                  section={section}
                  canvasInstanceUri={selectedInstanceUri}
                />
              ) : (
                <CanvasSectionCard
                  key={section.id}
                  section={section}
                  isExpanded={expandedSection === section.id}
                  isFocused={focusedSectionId === section.id}
                  onToggleExpand={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                  onFocus={() => handleSectionFocus(section.id)}
                  onSelectItem={(uri) => {
                    setSelectedItem({ uri, sectionId: section.id })
                    setShowPropertyPane(true)
                  }}
                  selectedUri={selectedItem?.sectionId === section.id ? selectedItem.uri : null}
                  onRequestCreate={() => setCreateForSection(section)}
                  linkedItems={sectionData.find(s => s.sectionId === section.id)?.linkedItems}
                  notes={sectionData.find(s => s.sectionId === section.id)?.notes}
                  pendingEntities={pendingEntities.filter(e => e.sectionId === section.id)}
                  onApproveEntity={(entity) => handleApproveEntity(entity, section.id)}
                  onDismissEntity={handleDismissEntity}
                  canvasInstanceUri={selectedInstanceUri ?? undefined}
                  accountId={currentAccount?.id}
                  projectId={currentProject?.id}
                  apiKey={apiKey}
                  canvasContext={canvasContext}
                  onAddSuggestion={handleAddSuggestion}
                  onOpenSuggestionsPanel={handleOpenSuggestions}
                />
              )
            )}
          </div>
        </div>

        {/* Suggestions Panel - Shows AI suggestions for focused section */}
        {showSuggestionsPanel && suggestionsForSection && currentAccount && currentProject && apiKey && (
          <SectionSuggestionsPanel
            section={suggestionsForSection}
            canvasInstanceUri={selectedInstanceUri!}
            accountId={currentAccount.id}
            projectId={currentProject.id}
            apiKey={apiKey}
            canvasContext={canvasContext}
            onClose={handleCloseSuggestions}
            onAddSuggestion={handleAddSuggestion}
            className="w-[380px] border-l border-warm-200 flex-shrink-0"
          />
        )}

        {/* Property Pane - Shows instance or item details */}
        {showPropertyPane && (
          <div className="w-[380px] flex-shrink-0 bg-white border-l border-warm-200 overflow-hidden flex flex-col">
            {selectedItem ? (
              // Item Detail Panel
              <CanvasInstanceDetail
                instanceUri={selectedItem.uri}
                sectionId={selectedItem.sectionId}
                sections={selectedTemplate.sections}
                onClose={() => {
                  setSelectedItem(null)
                  setShowPropertyPane(false)
                }}
                onNavigate={(uri) => {
                  const section = selectedTemplate.sections.find((s) =>
                    s.classUris.some((classUri) => uri.includes(classUri.split('#')[0]))
                  )
                  if (section) {
                    setSelectedItem({ uri, sectionId: section.id })
                  } else {
                    setSelectedItem({ uri, sectionId: selectedItem.sectionId })
                  }
                }}
              />
            ) : instanceDetails ? (
              // Canvas Instance Property Pane
              <CanvasInstancePropertyPane
                instance={instanceDetails}
                onClose={() => setShowPropertyPane(false)}
              />
            ) : null}
          </div>
        )}

        {/* Canvas Coach Panel - Hidden when embedded in workspace */}
        {!hideAuxiliaryPanels && showAIPanel && currentAccount && currentProject && (
          <CanvasCoachPanel
            accountId={currentAccount.id}
            projectId={currentProject.id}
            instanceId={selectedInstanceUri ?? undefined}
            canvasType={(TEMPLATE_TO_CANVAS_TYPE[selectedTemplate.id] || 'bmc') as any}
            currentSection={focusedSectionId ?? undefined}
            canvasContent={canvasContentString}
            onSectionFocus={setFocusedSectionId}
            onAddToCanvas={handleAddEntityToCanvas}
            onEntitiesChange={handleEntitiesChange}
            onControlsReady={handleControlsReady}
            showEntitiesInPanel={false}
            className="w-[400px] border-l border-warm-200 flex-shrink-0"
          />
        )}

        {/* Document Processing Panel (Entity-First via documents3) - Hidden when embedded in workspace */}
        {!hideAuxiliaryPanels && showDocumentPanel && (
          <UnifiedDocumentPanel
            sourceContext="canvas"
            isExpanded={showDocumentPanel}
            onToggleExpand={toggleDocumentPanel}
            onDocumentAnalyzed={handleDocumentAnalyzed}
            onEntitySelected={handleEntitySelected}
            showKeyboardHint={true}
          />
        )}
      </div>

      {/* Create Ontology Instance Dialog */}
      {createForSection && (
        <CreateInstanceDialog
          classUri={createForSection.classUris[0]}
          classLabel={createForSection.title}
          color={createForSection.color}
          bgColor={createForSection.bgColor}
          onClose={() => setCreateForSection(null)}
          onCreated={(uri, label) => {
            // Link the new entity to this canvas section so it appears immediately
            if (selectedInstanceUri) {
              linkItemToSection.mutate({
                canvasUri: selectedInstanceUri,
                sectionId: createForSection.id,
                itemUri: uri,
                itemLabel: label,
                itemType: createForSection.classUris[0],
              })
            }
          }}
        />
      )}

      {/* Create Canvas Instance Dialog */}
      {showCreateInstanceDialog && (
        <CreateCanvasInstanceDialog
          templateId={selectedTemplate.id}
          onClose={() => setShowCreateInstanceDialog(false)}
          onCreated={handleInstanceCreated}
        />
      )}
    </div>
  )
}

// ============================================================================
// CANVAS INSTANCE PROPERTY PANE
// ============================================================================

interface CanvasInstancePropertyPaneProps {
  instance: {
    uri: string
    name: string
    description?: string
    templateId: string
    status: string
    createdAt: string
    updatedAt: string
  }
  onClose: () => void
}

function CanvasInstancePropertyPane({ instance, onClose }: CanvasInstancePropertyPaneProps) {
  const updateInstance = useUpdateCanvasInstance()
  const [isEditingName, setIsEditingName] = useState(false)
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [editName, setEditName] = useState(instance.name)
  const [editDescription, setEditDescription] = useState(instance.description || '')

  const handleSaveName = async () => {
    if (editName.trim() && editName !== instance.name) {
      await updateInstance.mutateAsync({
        uri: instance.uri,
        updates: { name: editName.trim() },
      })
    }
    setIsEditingName(false)
  }

  const handleSaveDescription = async () => {
    if (editDescription !== instance.description) {
      await updateInstance.mutateAsync({
        uri: instance.uri,
        updates: { description: editDescription },
      })
    }
    setIsEditingDescription(false)
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return dateStr
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-warm-200 flex items-center justify-between">
        <h3 className="font-semibold text-text">Canvas Properties</h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-warm-100 rounded transition-colors"
        >
          <X className="w-4 h-4 text-text-muted" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 space-y-6">
        {/* Name */}
        <div>
          <label className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2 block">
            Name
          </label>
          {isEditingName ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName()
                  if (e.key === 'Escape') setIsEditingName(false)
                }}
                onBlur={handleSaveName}
                autoFocus
                className="flex-1 px-3 py-2 border border-warm-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          ) : (
            <div
              onClick={() => {
                setEditName(instance.name)
                setIsEditingName(true)
              }}
              className="px-3 py-2 bg-warm-50 rounded-lg text-sm cursor-pointer hover:bg-warm-100 transition-colors flex items-center justify-between group"
            >
              <span className="font-medium">{instance.name}</span>
              <Edit3 className="w-3.5 h-3.5 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2 block">
            Description
          </label>
          {isEditingDescription ? (
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setIsEditingDescription(false)
              }}
              onBlur={handleSaveDescription}
              autoFocus
              rows={3}
              className="w-full px-3 py-2 border border-warm-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
              placeholder="Add a description..."
            />
          ) : (
            <div
              onClick={() => {
                setEditDescription(instance.description || '')
                setIsEditingDescription(true)
              }}
              className="px-3 py-2 bg-warm-50 rounded-lg text-sm cursor-pointer hover:bg-warm-100 transition-colors min-h-[60px] group"
            >
              {instance.description ? (
                <span className="text-text">{instance.description}</span>
              ) : (
                <span className="text-text-muted italic">Click to add description...</span>
              )}
              <Edit3 className="w-3.5 h-3.5 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity float-right" />
            </div>
          )}
        </div>

        {/* Status */}
        <div>
          <label className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2 block">
            Status
          </label>
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-text-muted" />
            <Badge
              variant="outline"
              className={cn(
                instance.status === 'active' && 'bg-green-50 text-green-700 border-green-200',
                instance.status === 'draft' && 'bg-yellow-50 text-yellow-700 border-yellow-200',
                instance.status === 'archived' && 'bg-gray-50 text-gray-700 border-gray-200'
              )}
            >
              {instance.status}
            </Badge>
          </div>
        </div>

        {/* Metadata */}
        <div className="space-y-3 pt-4 border-t border-warm-200">
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <Calendar className="w-4 h-4" />
            <span>Created: {formatDate(instance.createdAt)}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <Calendar className="w-4 h-4" />
            <span>Updated: {formatDate(instance.updatedAt)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// CANVAS SECTION CARD
// ============================================================================

interface PendingEntity {
  type: string
  label: string
  description?: string
  sectionId?: string
  confidence: number
}

/** Item to display in a canvas section */
interface CanvasSectionItem {
  uri?: string // Optional - text items don't have URIs
  label: string
  isFromNotes?: boolean // True if parsed from notes text
}

interface CanvasSectionCardProps {
  section: CanvasSection
  isExpanded: boolean
  isFocused?: boolean
  onToggleExpand: () => void
  onFocus?: () => void
  onSelectItem: (uri: string) => void
  selectedUri: string | null
  onRequestCreate: () => void
  /** Linked items from section data */
  linkedItems?: Array<{ instanceUri: string; label: string; note?: string }>
  /** Notes text from section data */
  notes?: string
  pendingEntities?: PendingEntity[]
  onApproveEntity?: (entity: PendingEntity) => void
  onDismissEntity?: (entity: PendingEntity) => void
  /** AI Suggestions props (legacy popup support) */
  canvasInstanceUri?: string
  accountId?: string
  projectId?: string
  apiKey?: string
  canvasContext?: Record<string, string[]>
  onAddSuggestion?: (suggestion: { type: string; label: string; description?: string; sectionId: string; confidence: number }) => void
  /** Open suggestions panel (new) */
  onOpenSuggestionsPanel?: (section: CanvasSection) => void
}

function CanvasSectionCard({ section, isExpanded, isFocused, onToggleExpand, onFocus, onSelectItem, selectedUri, onRequestCreate, linkedItems = [], notes = '', pendingEntities = [], onApproveEntity, onDismissEntity, canvasInstanceUri, accountId, projectId, apiKey, canvasContext, onAddSuggestion, onOpenSuggestionsPanel }: CanvasSectionCardProps) {
  const Icon = section.icon
  const [showSuggestionsPopup, setShowSuggestionsPopup] = useState(false)
  const sparklesButtonRef = useRef<HTMLButtonElement>(null)

  // Derive items from linkedItems + parsed notes
  const items = useMemo(() => {
    const result: CanvasSectionItem[] = []

    // Add linked items (these have URIs and can be clicked)
    for (const item of linkedItems) {
      result.push({
        uri: item.instanceUri,
        label: item.label || item.note || 'Unnamed item',
        isFromNotes: false,
      })
    }

    // Parse notes into bullet points (these are text-only)
    if (notes.trim()) {
      const lines = notes.split('\n')
      for (const line of lines) {
        const trimmed = line.trim()
        if (trimmed) {
          // Remove bullet point prefixes (•, -, *)
          const label = trimmed.replace(/^[•\-\*]\s*/, '')
          if (label) {
            result.push({
              label,
              isFromNotes: true,
            })
          }
        }
      }
    }

    return result
  }, [linkedItems, notes])

  return (
    <div
      onClick={() => onFocus?.()}
      style={{
        gridArea: section.gridArea,
        backgroundColor: section.bgColor,
        borderRadius: '12px',
        border: `2px solid ${isFocused ? section.color : `${section.color}20`}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        boxShadow: isFocused ? `0 0 0 3px ${section.color}20` : 'none',
        cursor: 'pointer',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 14px',
          borderBottom: `1px solid ${section.color}20`,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexShrink: 0,
        }}
      >
        <div
          onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, cursor: 'pointer' }}
        >
          <Icon style={{ width: '18px', height: '18px', color: section.color, flexShrink: 0 }} />
          <span style={{ flex: 1, fontWeight: 600, fontSize: '13px', color: section.color }}>
            {section.title}
          </span>
          <Badge style={{ backgroundColor: section.color, color: 'white', fontSize: '10px' }}>
            {items.length}{pendingEntities.length > 0 && <span style={{ opacity: 0.7 }}> +{pendingEntities.length}</span>}
          </Badge>
          <ChevronDown
            style={{
              width: '14px',
              height: '14px',
              color: section.color,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        </div>
        {/* Sparkles button for AI suggestions - opens panel if available, else popup */}
        {canvasInstanceUri && accountId && projectId && apiKey && (onAddSuggestion || onOpenSuggestionsPanel) && (
          <div style={{ position: 'relative' }}>
            <button
              ref={sparklesButtonRef}
              onClick={(e) => {
                e.stopPropagation()
                // Use panel if available (preferred), otherwise use popup
                if (onOpenSuggestionsPanel) {
                  onOpenSuggestionsPanel(section)
                } else {
                  setShowSuggestionsPopup(!showSuggestionsPopup)
                }
              }}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '6px',
                border: `1px solid ${showSuggestionsPopup ? section.color : section.color + '40'}`,
                backgroundColor: showSuggestionsPopup ? `${section.color}15` : 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: showSuggestionsPopup ? 1 : 0.7,
                transition: 'opacity 0.15s, background-color 0.15s, border-color 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
              onMouseLeave={(e) => { if (!showSuggestionsPopup) e.currentTarget.style.opacity = '0.7' }}
              title="Get AI suggestions"
            >
              <Sparkles style={{ width: '13px', height: '13px', color: section.color }} />
            </button>
            {/* Legacy popup - only shown if panel is not available */}
            {showSuggestionsPopup && !onOpenSuggestionsPanel && onAddSuggestion && (
              <SectionSuggestionsPopup
                section={section}
                canvasInstanceUri={canvasInstanceUri}
                accountId={accountId}
                projectId={projectId}
                apiKey={apiKey}
                canvasContext={canvasContext}
                isOpen={showSuggestionsPopup}
                onClose={() => setShowSuggestionsPopup(false)}
                onAddSuggestion={onAddSuggestion}
                anchorRef={sparklesButtonRef}
              />
            )}
          </div>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRequestCreate()
          }}
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '6px',
            border: `1px solid ${section.color}40`,
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            opacity: 0.7,
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
          title={`Add ${section.title}`}
        >
          <Plus style={{ width: '14px', height: '14px', color: section.color }} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
        {items.length === 0 && pendingEntities.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '16px' }}>
            <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', textAlign: 'center' }}>
              No data yet
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {/* Pending entities from AI coach - shown first with special styling */}
            {pendingEntities.map((entity, index) => (
              <div
                key={`pending-${entity.label}-${index}`}
                style={{
                  padding: '4px 8px 4px 10px',
                  backgroundColor: `${section.color}10`,
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '11px',
                  border: `1.5px dashed ${section.color}50`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span style={{ color: section.color, fontWeight: 500, flex: 1 }}>
                  {entity.label}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onApproveEntity?.(entity)
                  }}
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--color-success)',
                    backgroundColor: 'rgba(92, 138, 107, 0.1)',
                    color: 'var(--color-success)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                    transition: 'all 0.15s',
                  }}
                  title="Add to canvas"
                >
                  ✓
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onDismissEntity?.(entity)
                  }}
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--color-error)',
                    backgroundColor: 'rgba(184, 99, 111, 0.1)',
                    color: 'var(--color-error)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                    transition: 'all 0.15s',
                  }}
                  title="Dismiss"
                >
                  ×
                </button>
              </div>
            ))}
            {/* Section items (from linkedItems + notes) */}
            {items.map((item, index) => (
              <div
                key={item.uri || `note-${index}`}
                onClick={() => item.uri && onSelectItem(item.uri)}
                style={{
                  padding: '6px 10px',
                  backgroundColor: item.uri && selectedUri === item.uri ? section.color : 'white',
                  color: item.uri && selectedUri === item.uri ? 'white' : 'var(--color-text)',
                  borderRadius: '6px',
                  fontSize: '11px',
                  cursor: item.uri ? 'pointer' : 'default',
                  border: item.isFromNotes
                    ? `1px solid ${section.color}30`
                    : `1px solid ${section.color}40`,
                  transition: 'all 0.15s',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  opacity: item.isFromNotes ? 0.9 : 1,
                }}
              >
                {item.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Expanded Description */}
      {isExpanded && (
        <div style={{ padding: '8px 12px', borderTop: `1px solid ${section.color}20`, backgroundColor: 'white' }}>
          <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
            {section.description}
          </p>
          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {section.classUris.map((uri) => {
              const cls = ALL_CLASSES.find((c) => c.uri === uri)
              return cls ? (
                <Badge key={uri} variant="outline" style={{ fontSize: '10px', fontFamily: 'monospace' }}>
                  {cls.localName}
                </Badge>
              ) : (
                <Badge key={uri} variant="outline" style={{ fontSize: '10px', fontFamily: 'monospace', opacity: 0.6 }}>
                  {getLocalName(uri)}
                </Badge>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// CANVAS INSTANCE DETAIL
// ============================================================================

interface CanvasInstanceDetailProps {
  instanceUri: string
  sectionId: string
  sections: CanvasSection[]
  onClose: () => void
  onNavigate: (uri: string) => void
}

function CanvasInstanceDetail({ instanceUri, sectionId, sections, onClose, onNavigate }: CanvasInstanceDetailProps) {
  const queryClient = useQueryClient()

  // Find section by ID to get colors
  const section = sections.find((s) => s.id === sectionId)
  const color = section?.color || '#847561'
  const bgColor = section?.bgColor

  const handleDeleted = () => {
    // Invalidate canvas instances
    queryClient.invalidateQueries({ queryKey: ['canvasInstances'] })
    onClose()
  }

  return (
    <InstanceDetailPanel
      instanceUri={instanceUri}
      color={color}
      bgColor={bgColor}
      onClose={onClose}
      onNavigate={onNavigate}
      onDeleted={handleDeleted}
    />
  )
}
